import { useState, useContext, useRef, useEffect } from 'react';
import ThemeContext from '../contexts/ThemeContext';
import styled from 'styled-components';
import i18n from '../i18n';
import Icon from './Icon';
import VoiceVisualizerView from './VoiceVisualizerView';

const breakpoints = {
  tablet: '768px',
  mobile: '480px',
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-family: sans-serif;
  height: 100%;
  width: 100%;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${({ backgroundColor }) => (backgroundColor)};
  width: 100%;
  padding: 40px;
`;

const LogoContainer = styled.div`
  display: flex;
  width: 100%;
  box-sizing: border-box;
`;

const StyledAiTitle = styled.h2`
  color: ${({ color }) => (color)};
  margin: 0;
  font-size: 1.5em;
  font-weight: bold;
`;

const MessagePair = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;

  @media (max-width: ${breakpoints.tablet}) {
    flex-direction: column;
  }
`;

const ChatArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px; 
  flex: 1;
  padding: 40px;
  overflow-y: auto;
  align-items: flex-start;
`;

const Message = styled.div`
  display: flex;
  flex-flow: column;
  flex: 1 1 calc(50% - 20px); 
  box-sizing: border-box;

  @media (max-width: ${breakpoints.tablet}) {
    flex: 1 1 100%; 
  }
`;

const MessageBlock = styled.div`
  display: flex;
  margin-bottom: 10px;
`;

const MessageText = styled.p`
  background-color: ${({ backgroundColor }) => (backgroundColor)};
  padding: 10px;
  border-radius: 8px;
  flex: 1;
  margin: 0;
  color: ${({ color }) => (color)};
`;

const InputArea = styled.div`
  display: flex;
  justify-content: center;
  padding: 10px;
  background-color: ${({ backgroundColor }) => (backgroundColor)};
  margin: 20px auto;
  border-radius: 8px;
  width: 100%;
  max-width: 900px;
`;

const Input = styled.input`
  flex: 1;
  background: none;
  width: 100%;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  font-size: 1.2em;
  cursor: pointer;
  padding: 0;
`;

const IconActionButton = styled(IconButton)`
  margin-left: 5px;
`;

const StyledIcon = styled(Icon)`
  & > i, & > span {
    color: ${({ color }) => (color)} !important;
  }
`;

const StyledPlayIcon = styled(StyledIcon)`
  background-color: ${({ backgroundColor }) => (backgroundColor)};
  border-radius: 8px;
  width: 30px;
  height: 30px;
`;

const LanguageIcon = styled.div`
  background-color: ${({ backgroundColor }) => (backgroundColor)};
  color: ${({ color }) => (color)};
  margin-right: 10px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  text-transform: uppercase;
`;

function toPairs(arr) {
  const result = [];
  for (let i = 0; i < arr.length; i += 2) {
    result.push(arr.slice(i, i + 2));
  }
  return result;
}

const AIRoomView = ({messages, handleSend, handleRecordClick, handleClose, playMessage, isPlaying, playingMessageUrl, handlePauseRecording, handleResumedRecording, onAudioStreamAvailable, isRecording}) => {  
  const [input, setInput] = useState('');
  const {colors} = useContext(ThemeContext);
  const messagesContainerRef = useRef(null);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <Container>
      <Header backgroundColor={colors.backgroundColorAi}>
        <LogoContainer>
          <StyledAiTitle color={colors.tulkaMainColor}>{i18n('od_type_ai_subtitle')}</StyledAiTitle>
        </LogoContainer>
        <IconButton onClick={handleClose}>{i18n('close')}</IconButton>
      </Header>
      <ChatArea ref={messagesContainerRef}>
        {toPairs(messages).map((msgPair, index) => (
          <MessagePair key={index}>
            {msgPair.map((msg, msgIndex) => (
              <Message key={`${index}-${msgIndex}`}>
              <MessageBlock>
                <LanguageIcon 
                  backgroundColor={colors.backgroundColorAi}
                  color={colors.tulkaMainColor}
                >{msg.language}</LanguageIcon>
                <MessageText 
                  backgroundColor={colors.backgroundColorAi}
                  color={msg.error ? colors.error: colors.tulkaMainColor}
                >{msg.text}</MessageText>
              </MessageBlock>
              {msg.audioUrl ? (
                <div style={{marginLeft: 'auto'}}>            
                  <IconActionButton onClick={() => playMessage(msg.audioUrl)}>
                    {isPlaying && playingMessageUrl === msg.audioUrl ? (<StyledPlayIcon
                      backgroundColor={colors.backgroundColorAi}
                      color={colors.tulkaMainColor}
                      margin={10}
                      iconLib={'material'}
                      icon={'pause'}
                    />) : (<StyledPlayIcon
                      backgroundColor={colors.backgroundColorAi}
                      color={colors.tulkaMainColor}
                      margin={10}
                      iconLib={'material'}
                      icon={'play_arrow'}
                    />)}
                  </IconActionButton> 
                </div>
              ) : null}
            </Message>
            ))}
          </MessagePair>
          
        ))}
      </ChatArea>
      <InputArea backgroundColor={colors.backgroundColorAi}>
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && input.trim().length > 1) {
              handleSend(input);
              setInput('');
            }
          }}
          placeholder={i18n('chatTypeMessage')}
          aria-label={i18n('chatTypeMessage')}
        />
        <IconButton onClick={()=>{
          handleSend(input);
          setInput('');
        }}>
          {input.length > 1 ? (
            <StyledIcon
              color={colors.tulkaMainColor}
              margin={10}
              iconLib={'material'}
              icon={'send'}
            />
          ) : (
            <StyledIcon
              color={colors.tulkaMainColor}
              margin={10}
              iconLib={'material'}
              icon={'keyboard'}
            />
          )}
        </IconButton>
      </InputArea>
      <VoiceVisualizerView
        onStartRecording={handleRecordClick} 
        onStopRecording={handleRecordClick}
        onPausedRecording={handlePauseRecording}
        onResumedRecording={handleResumedRecording}
        onAudioStreamAvailable={onAudioStreamAvailable}
        isRecording={isRecording}
      />
    </Container>
  );
};

export default AIRoomView;